import { useEffect, useState } from 'react';
import { isAppId, type Locale } from '@appwin/shared';
import { useAppDetail } from './api.js';
import { AppPage } from './pages/AppPage.js';
import { CatalogPage } from './pages/CatalogPage.js';
import { DownloadPage, NotFoundPage } from './pages/StaticPages.js';
import { Skeleton, Page } from '@appwin/ui';

export interface Route {
  kind: 'catalog' | 'app' | 'download' | 'notFound';
  appId?: string;
}

/**
 * Minimal history router. The site is tiny (three routes), so a dependency-free
 * router is preferable to adding one; navigation intercepts same-origin links
 * and calls `pushState`.
 */
export function parsePath(pathname: string): Route {
  const stripped = pathname.replace(/^\/ru(?=\/|$)/, '') || '/';
  if (stripped === '/') return { kind: 'catalog' };
  if (stripped === '/download') return { kind: 'download' };
  if (stripped.startsWith('/apps/')) {
    const appId = stripped.slice('/apps/'.length).replace(/\/$/, '');
    // `isAppId` is the single source of truth: it also accepts dotted winget
    // slugs such as `winget/7zip.7zip`.
    if (isAppId(appId)) return { kind: 'app', appId };
  }
  return { kind: 'notFound' };
}

export function App({ locale }: { locale: Locale }) {
  const [route, setRoute] = useState<Route>(() => parsePath(window.location.pathname));

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || anchor.target || anchor.hasAttribute('download')) return;
      if (anchor.origin !== window.location.origin) return;
      event.preventDefault();
      window.history.pushState({}, '', anchor.pathname);
      setRoute(parsePath(anchor.pathname));
      window.scrollTo({ top: 0 });
    };
    const onPop = () => setRoute(parsePath(window.location.pathname));
    document.addEventListener('click', onClick);
    window.addEventListener('popstate', onPop);
    return () => {
      document.removeEventListener('click', onClick);
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  switch (route.kind) {
    case 'app':
      return <AppRoute locale={locale} appId={route.appId ?? null} />;
    case 'download':
      return <DownloadPage locale={locale} />;
    case 'catalog':
      return <CatalogPage locale={locale} />;
    default:
      return <NotFoundPage locale={locale} />;
  }
}

function AppRoute({ locale, appId }: { locale: Locale; appId: string | null }) {
  const { data, loading, error } = useAppDetail(locale, appId);
  if (loading) {
    return (
      <Page>
        <div className="py-6">
          <Skeleton className="h-64" />
        </div>
      </Page>
    );
  }
  if (error?.status === 404 || !data) return <NotFoundPage locale={locale} />;
  return <AppPage locale={locale} app={data} />;
}
